import { useEffect, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Paper } from "@mui/material";
import { useTheme } from "next-themes";
import { colors } from "../utils/exampleData";

const ExerciseChart = ({
  exerciseData,
  useOneRM,
  currentChart,
  weightUnit,
  adjustRPE,
}) => {
  const checkDesktop = () => window.innerWidth > 640;
  const [isDesktop, setIsDesktop] = useState(checkDesktop());

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(checkDesktop());
    };

    window.addEventListener("resize", handleResize);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Organize data for chart
  const chartData = [];

  const exercises = {};

  const { theme, setTheme } = useTheme();

  // Group exercises by name
  exerciseData.forEach((exercise) => {
    const exerciseName = exercise.exerciseName;
    if (!exercises[exerciseName]) {
      exercises[exerciseName] = [];
    }
    exercises[exerciseName].push(exercise);
  });

  // Prepare data for each exercise
  Object.keys(exercises).forEach((exerciseName, index) => {
    const exerciseData = exercises[exerciseName];

    // Sort exercise data by date
    exerciseData.sort((a, b) => new Date(a.date) - new Date(b.date));

    // Populate chart data
    const dataPoints = exerciseData.map((exercise) => ({
      date: new Date(exercise.date).toLocaleDateString(),
      [`weight${index + 1}`]: parseFloat(exercise.weight),
      [`reps${index + 1}`]: parseFloat(exercise.reps),
      [`rpe${index + 1}`]: parseFloat(exercise.rpe),
    }));

    chartData.push({
      name: exerciseName.toLowerCase(),
      data: dataPoints,
    });
  });
  const uniqueExerciseData = [
    ...new Set(exerciseData.map((exercise) => exercise.exerciseName)),
  ];
  const containerHeight = 300 + 30 * Math.max(uniqueExerciseData.length, 3);
  return (
    <Paper
      sx={{
        width: { xs: "100%", sm: "90%", md: "80%", xl: "100%" },
        borderRadius: { xs: 8, lg: 11 },
      }}
    >
      <div
        className={`
        w-full ${
          currentChart === 0
            ? "h-[410px] sm:h-[450px] md:h-[520px]"
            : "h-[350px] sm:h-[450px] md:h-[520px]"
        } overflow-y-auto overflow-x-hidden transition-all duration-300 ease-in-out 
        
      `}
      >
        <ResponsiveContainer
          width="100%"
          height={isDesktop || currentChart === 1 ? "100%" : containerHeight}
          className="pt-8 px-2 py-2 md:py-6 md:px-6"
        >
          <AreaChart data={chartData.flatMap((exercise) => exercise.data)}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" tickMargin={10} />
            {chartData.length > 0 && (
              <YAxis
                unit={weightUnit === "imperial" ? "lbs" : "kg"}
                type="number"
                domain={[
                  (dataMin) => Math.round(dataMin - 0.05 * dataMin, "dataMax"),
                ]}
              />
            )}
            <Tooltip
              labelStyle={{
                fontSize: isDesktop ? "1rem" : "0.8rem",
                whiteSpace: "normal",
              }}
              itemStyle={{
                textTransform: "capitalize",
                fontWeight: 600,
                fontSize: isDesktop ? "1rem" : "0.8rem",
                whiteSpace: "normal",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              wrapperStyle={{
                color: "inherit",
                textOverflow: "ellipsis",
                maxWidth: "80%",
              }}
              contentStyle={{
                borderColor: theme === "dark" ? "#3f3f46" : "#cbd5e1",
                opacity: 0.9,
                borderRadius: 15,
                backgroundColor: theme === "dark" ? "rgba(0,0,0,0.9)" : "white",
                maxWidth: "350px",
              }}
              formatter={(value, name, props) => {
                const weightValue = `${value} ${
                  weightUnit === "imperial" ? "lbs" : "kg"
                }`;
                const repsValue =
                  props.payload[`reps${props.dataKey.slice(-1)}`];
                const rpeValue = props.payload[`rpe${props.dataKey.slice(-1)}`];

                let label = weightValue;

                if (
                  repsValue !== undefined &&
                  !useOneRM &&
                  currentChart !== 1
                ) {
                  label += ` - ${repsValue} reps`;
                } else if (useOneRM) {
                  label += ` (1RM)`;
                }

                if (rpeValue && !useOneRM) {
                  label += ` @RPE ${rpeValue}`;
                } else if (adjustRPE && rpeValue) {
                  label += ` Adjusted for RPE`;
                }

                return [label, name];
              }}
            />
            <Legend
              wrapperStyle={{
                textTransform: "capitalize",
                paddingTop: "25px",
              }}
            />
            {chartData.map((exercise, index) => (
              <Area
                key={exercise.name}
                type="monotone"
                dataKey={`weight${index + 1}`}
                name={exercise.name}
                fill={colors[index % colors.length]}
                stroke={colors[index % colors.length]}
                dot={{ strokeWidth: 1 }}
                animationEasing="ease-out"
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Paper>
  );
};

export default ExerciseChart;
